import React, { useMemo, useState } from 'react';
import { Clock, ArrowRight, Search, X, LayoutGrid, List } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Pagination } from '../components/ui/Pagination';
import { BLOG_CATEGORIES_I18N, BLOG_UI, formatBlogDate } from '../data/blogArticles';
import { useLanguage } from '../i18n/LanguageContext';
import { useBlog } from '../contexts/BlogContext';
import { AdSlot } from '../components/ads/AdSlot';
interface BlogListPageProps {
  onNavigate: (page: string) => void;
}
export function BlogListPage({
  onNavigate
}: BlogListPageProps) {
  const {
    language
  } = useLanguage();
  const {
    allArticles
  } = useBlog();
  const ui = BLOG_UI[language];
  const categories = BLOG_CATEGORIES_I18N[language];
  const ruCategories = BLOG_CATEGORIES_I18N.ru;
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0); // 0 is 'All'
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const articlesPerPage = viewMode === 'cards' ? 6 : 10;
  // Filter articles
  const filteredArticles = useMemo(() => {
    return allArticles.filter(article => {
      // Category filter (using Russian category names from data)
      if (activeCategoryIndex !== 0 && article.category !== ruCategories[activeCategoryIndex]) {
        return false;
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return article.title.toLowerCase().includes(query) || article.excerpt.toLowerCase().includes(query) || article.tags.some(tag => tag.toLowerCase().includes(query));
      }
      return true;
    });
  }, [activeCategoryIndex, searchQuery, ruCategories, allArticles]);
  // Pagination
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const paginatedArticles = filteredArticles.slice((currentPage - 1) * articlesPerPage, currentPage * articlesPerPage);
  // Featured article logic
  const isDefaultView = activeCategoryIndex === 0 && !searchQuery && currentPage === 1 && viewMode === 'cards';
  const featuredArticle = isDefaultView ? allArticles.find(a => a.featured) || allArticles[0] : null;
  // Exclude featured article from grid if in default view
  const displayArticles = isDefaultView ? paginatedArticles.filter(a => a.id !== featuredArticle?.id).slice(0, articlesPerPage) : paginatedArticles;
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  const handleCategoryClick = (index: number) => {
    setActiveCategoryIndex(index);
    setCurrentPage(1);
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-17625">
      <div className="bg-white border-b border-slate-200 pt-12 pb-12" data-id="element-17626">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-17627">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8" data-id="element-17628">
            <div data-id="element-17629">
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-17630">
                {ui.title}
              </h1>
              <p className="text-lg text-slate-600" data-id="element-17631">{ui.subtitle}</p>
            </div>

            <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-17632">
              <div className="relative w-full md:w-72" data-id="element-17633">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" data-id="element-17634" />
                <input type="text" value={searchQuery} onChange={handleSearch} placeholder={ui.search} className="w-full pl-10 pr-10 py-2.5 rounded-xl border-2 border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all" data-id="element-17635" />
                {searchQuery && <button onClick={() => handleSearch({
                target: {
                  value: ''
                }
              } as any)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-17636">
                    <X className="w-4 h-4" data-id="element-17637" />
                  </button>}
              </div>

              <div className="hidden sm:flex items-center bg-slate-100 p-1 rounded-xl" data-id="element-17638">
                <button onClick={() => {
                setViewMode('cards');
                setCurrentPage(1);
              }} className={`p-2 rounded-lg transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700'}`} title={ui.viewCards} data-id="element-17639">
                  <LayoutGrid className="w-5 h-5" data-id="element-17640" />
                </button>
                <button onClick={() => {
                setViewMode('table');
                setCurrentPage(1);
              }} className={`p-2 rounded-lg transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-primary' : 'text-slate-500 hover:text-slate-700'}`} title={ui.viewTable} data-id="element-17641">
                  <List className="w-5 h-5" data-id="element-17642" />
                </button>
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="flex overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 gap-2 hide-scrollbar" data-id="element-17643">
            {categories.map((cat, index) => <button key={cat} onClick={() => handleCategoryClick(index)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategoryIndex === index ? 'bg-slate-900 text-white shadow-md' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-17644">
                {cat}
              </button>)}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-17645">
        {/* Featured Article (Cards view only) */}
        {featuredArticle && viewMode === 'cards' && <Card hoverable padding="none" className="mb-12 cursor-pointer group overflow-hidden" onClick={() => onNavigate(`blog-post/${featuredArticle.slug}`)} data-id="element-17646">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-17647">
              <div className={`h-64 md:h-auto ${featuredArticle.coverColor} relative overflow-hidden flex items-center justify-center`} data-id="element-17648">
                {featuredArticle.coverImage ? <img src={featuredArticle.coverImage} alt={featuredArticle.title} className="w-full h-full object-cover" data-id="element-17649" /> : <>
                    <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/10 to-transparent" data-id="element-17650"></div>
                    <span className="text-4xl md:text-6xl font-black text-slate-900/10 uppercase tracking-widest rotate-[-10deg]" data-id="element-17651">
                      {featuredArticle.category}
                    </span>
                  </>}
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-17652">
                <Badge variant="pro" className="w-fit mb-4" data-id="element-17653">
                  {ui.featured}
                </Badge>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 group-hover:text-primary transition-colors" data-id="element-17654">
                  {featuredArticle.title}
                </h2>
                <p className="text-slate-600 mb-6 line-clamp-3 leading-relaxed" data-id="element-17655">
                  {featuredArticle.excerpt}
                </p>
                <div className="flex items-center text-sm text-slate-500 mt-auto" data-id="element-17656">
                  <span data-id="element-17657">{formatBlogDate(featuredArticle.date, language)}</span>
                  <span className="mx-2" data-id="element-17658">•</span>
                  <span className="flex items-center" data-id="element-17659">
                    <Clock className="w-4 h-4 mr-1" data-id="element-17660" />{' '}
                    {featuredArticle.readTime} {ui.readTime}
                  </span>
                </div>
              </div>
            </div>
          </Card>}

        {/* Results Info */}
        {(searchQuery || activeCategoryIndex !== 0) && <div className="mb-8 text-slate-600" data-id="element-17661">
            {ui.found}:{' '}
            <span className="font-bold text-slate-900" data-id="element-17662">
              {filteredArticles.length}
            </span>
          </div>}

        {/* Content */}
        {displayArticles.length > 0 ? viewMode === 'cards' /* Grid View */ ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12" data-id="element-17663">
              {displayArticles.map(article => <Card key={article.id} hoverable padding="none" className="flex flex-col cursor-pointer group overflow-hidden" onClick={() => onNavigate(`blog-post/${article.slug}`)} data-id="element-17664">
                  <div className={`h-48 ${article.coverColor} relative overflow-hidden flex items-center justify-center`} data-id="element-17665">
                    {article.coverImage ? <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover" data-id="element-17666" /> : <>
                        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/5 to-transparent" data-id="element-17667"></div>
                        <span className="text-3xl font-black text-slate-900/10 uppercase tracking-widest rotate-[-10deg]" data-id="element-17668">
                          {article.category}
                        </span>
                      </>}
                  </div>
                  <div className="p-6 flex flex-col flex-grow" data-id="element-17669">
                    <Badge variant="neutral" className="w-fit mb-3" data-id="element-17670">
                      {article.category}
                    </Badge>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-3 group-hover:text-primary transition-colors line-clamp-2" data-id="element-17671">
                      {article.title}
                    </h3>
                    <p className="text-slate-600 mb-6 line-clamp-3 text-sm flex-grow leading-relaxed" data-id="element-17672">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-slate-500 mt-auto pt-4 border-t border-slate-100" data-id="element-17673">
                      <span data-id="element-17674">{formatBlogDate(article.date, language)}</span>
                      <span className="flex items-center" data-id="element-17675">
                        <Clock className="w-4 h-4 mr-1" data-id="element-17676" /> {article.readTime}
                      </span>
                    </div>
                  </div>
                </Card>)}
            </div> /* Table View */ : <div className="mb-12" data-id="element-17677">
              {/* Desktop Table */}
              <div className="hidden md:block bg-white rounded-2xl border border-slate-200 overflow-hidden" data-id="element-17678">
                <table className="w-full text-left border-collapse" data-id="element-17679">
                  <thead data-id="element-17680">
                    <tr className="bg-slate-50 border-b border-slate-200 text-sm font-semibold text-slate-600" data-id="element-17681">
                      <th className="py-4 px-6" data-id="element-17682">{ui.tableTitle}</th>
                      <th className="py-4 px-6 w-48" data-id="element-17683">{ui.tableCategory}</th>
                      <th className="py-4 px-6 w-40" data-id="element-17684">{ui.tableDate}</th>
                      <th className="py-4 px-6 w-32" data-id="element-17685">{ui.tableReadTime}</th>
                      <th className="py-4 px-6 w-16" data-id="element-17686"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-17687">
                    {displayArticles.map(article => <tr key={article.id} onClick={() => onNavigate(`blog-post/${article.slug}`)} className="hover:bg-slate-50 transition-colors cursor-pointer group" data-id="element-17688">
                        <td className="py-4 px-6" data-id="element-17689">
                          <div className="font-bold text-slate-900 mb-1 group-hover:text-primary transition-colors" data-id="element-17690">
                            {article.title}
                          </div>
                          <div className="text-sm text-slate-500 line-clamp-1" data-id="element-17691">
                            {article.excerpt}
                          </div>
                        </td>
                        <td className="py-4 px-6" data-id="element-17692">
                          <Badge variant="neutral" data-id="element-17693">{article.category}</Badge>
                        </td>
                        <td className="py-4 px-6 text-sm text-slate-600" data-id="element-17694">
                          {formatBlogDate(article.date, language)}
                        </td>
                        <td className="py-4 px-6 text-sm text-slate-600" data-id="element-17695">
                          {article.readTime}
                        </td>
                        <td className="py-4 px-6 text-right" data-id="element-17696">
                          <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-primary transition-colors" data-id="element-17697" />
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              {/* Mobile List View (for table mode) */}
              <div className="md:hidden space-y-4" data-id="element-17698">
                {displayArticles.map(article => <Card key={article.id} hoverable padding="md" onClick={() => onNavigate(`blog-post/${article.slug}`)} className="cursor-pointer group" data-id="element-17699">
                    <div className="flex justify-between items-start mb-2" data-id="element-17700">
                      <Badge variant="neutral" className="text-xs" data-id="element-17701">
                        {article.category}
                      </Badge>
                      <span className="text-xs text-slate-500" data-id="element-17702">
                        {formatBlogDate(article.date, language)}
                      </span>
                    </div>
                    <h3 className="font-bold text-slate-900 group-hover:text-primary transition-colors mb-2" data-id="element-17703">
                      {article.title}
                    </h3>
                    <div className="flex items-center justify-between text-xs text-slate-500 mt-4" data-id="element-17704">
                      <span className="flex items-center" data-id="element-17705">
                        <Clock className="w-3.5 h-3.5 mr-1" data-id="element-17706" />{' '}
                        {article.readTime}
                      </span>
                      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-primary" data-id="element-17707" />
                    </div>
                  </Card>)}
              </div>
            </div> : <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 mb-12" data-id="element-17708">
            <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" data-id="element-17709" />
            <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-17710">
              {ui.notFound}
            </h3>
            <p className="text-slate-500 mb-6" data-id="element-17711">{ui.notFoundHint}</p>
            <button onClick={() => {
          setSearchQuery('');
          setActiveCategoryIndex(0);
        }} className="text-primary font-medium hover:underline" data-id="element-17712">
              {ui.resetFilters}
            </button>
          </div>}

        {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-17713" />}

        {/* Blog Ad Banner */}
        <div className="mt-12" data-id="element-17714">
          <AdSlot slotId="SLOT-09" onNavigate={onNavigate} data-id="element-17715" />
        </div>
      </div>
    </div>;
}